import { FC, FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import Button from "@/components/Button";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";
import { api } from "@/utils/api";
import { FormLayout, Larger } from "@/components/Forms";
import useOnCloseWithConfirmation from "@/hooks/useOnCloseWithConfirmation";
import FormField from "@/components/Forms/FormField";
import { ReportResource } from "@/types/resources";
import Note from "@/components/Forms/Note";
import RestrictionsField from "./RestrictionsField";

interface FormDataT {
  name: string;
  file: File | null;
  note: string;
  is_restricted: boolean;
}

interface Props {
  onClose: () => void;
  groupId: number;
  editableReport?: ReportResource;
  isGroupRestricted: boolean;
}

const ReportForm: FC<Props> = ({
  onClose,
  editableReport,
  groupId,
  isGroupRestricted,
}) => {
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    clearErrors,
    setError,
    control,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      name: editableReport?.name ?? "",
      file: null,
      note: editableReport?.note ?? "",
      is_restricted: isGroupRestricted || !!editableReport?.isRestricted,
    },
  });

  const alerts = useAlerts();

  const closeDialog = useOnCloseWithConfirmation(onClose, isDirty);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const formData = new FormData();

        formData.append("reports_group_id", groupId.toString());
        formData.append("name", data.name);
        formData.append("note", data.note);
        formData.append("is_restricted", data.is_restricted ? "1" : "0");

        if (data.file) {
          formData.append("file", data.file);
        }

        if (editableReport) {
          formData.append("_method", "PUT");

          await api.post<FormData, { id: number }>(
            `/v1/reports/${editableReport.id}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
        } else {
          await api.post<FormData, { id: number }>("/v1/reports", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
        }

        queryClient.invalidateQueries(["reports"]);
        queryClient.invalidateQueries(["reports_groups"]);

        alerts.success(
          `Report was successfully ${!!editableReport ? "updated" : "created"}`
        );

        onClose();
      } catch (err) {
        handleFormErrors(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        heading={`${editableReport ? "Edit" : "Add"} Report`}
        onClose={closeDialog}
      >
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <Note textContent="<ul><li>Use index.html as a starting point inside your zip</li><li>Use relative paths for assets</li></ul>" />
              </Larger>
              <FormField<FormDataT>
                control={control}
                name="name"
                label="Report date"
                placeholder="01/12/2022"
              />
              <FormField<FormDataT>
                control={control}
                name="file"
                type="file"
                id="report-file-input"
                label="File"
                notes={<>Format: ZIP</>}
                accept=".zip"
                existingMedia={editableReport?.zip}
              />
              <FormField<FormDataT>
                control={control}
                name="note"
                type="textarea"
                label="Note"
              />
              {!isGroupRestricted && (
                <RestrictionsField<FormDataT>
                  name="is_restricted"
                  control={control}
                  description="This report will be accessible for limited Backbone roles only."
                />
              )}
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {editableReport ? "Update" : "Add"}
            </Button>
            <Button color="secondary" onClick={closeDialog} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default ReportForm;
